/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import {
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Pagination,
    PaginationItem,
} from "@mui/material";
import { ReactComponent as PreviousIcon } from "cms/assets/icons/previous.svg";
import { ReactComponent as NextIcon } from "cms/assets/icons/next.svg";
import Dropdown from "cms/shared/dropdown";
import { columns } from "./columns.js";

const Table = (props) => {
    const [tableData, setTableData] = useState([]);

    // Use props for pagination instead of local state
    const currentPageNumber = props?.currentPage || 1;
    const itemsPerPage = props?.pageSize || 10;
    const totalItemCount = props?.pagination?.total || 0;

    // Helper functions for CSS class names
    const getRowClassName = (rowIndex) => {
        return rowIndex % 2 === 0 ? "table-row-even" : "table-row-odd";
    };

    const getHeaderCellClassName = (columnIndex, totalColumns) => {
        const baseClass = "table-header-cell";
        const borderClass = columnIndex < totalColumns - 1 ? "has-border-right" : "";
        return `${baseClass} ${borderClass}`.trim();
    };

    const getBodyCellClassName = (columnIndex, totalColumns) => {
        const baseClass = "table-body-cell";
        const borderClass = columnIndex < totalColumns - 1 ? "has-border-right" : "";
        return `${baseClass} ${borderClass}`.trim();
    };

    // Use data directly from props (server-side pagination)
    useEffect(() => {
        setTableData(props?.data || []);
    }, [props?.data]);

    const handlePageChange = (event, newPageNumber) => {
        props?.onPageChange?.(event, newPageNumber);
    };

    const handleItemsPerPageChange = (event) => {
        props?.onPageSizeChange?.(event);
    };

    const totalPageCount = Math.ceil(totalItemCount / itemsPerPage);

    // Function to render evidence
    const renderEvidenceLinks = (evidenceArray) => {
        if (!evidenceArray || evidenceArray.length === 0) {
            return "";
        }

        return (
            <Box className="evidence-container">
                {evidenceArray?.map((evidenceItem, itemIndex) => {
                    return (
                        <Box
                            key={`${evidenceItem?.filename}-${itemIndex}`}
                            component="a"
                            href={evidenceItem?.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="evidence-link"
                        >
                            {evidenceItem?.title || evidenceItem?.filename}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // Function to format date and time
    const formatDateTime = (dataRow, fieldType) => {
        if (fieldType === "date") {
            const dateValue = dataRow?.created_at;
            if (dateValue) {
                const date = new Date(dateValue);
                return date.toLocaleDateString("en-CA"); // YYYY-MM-DD format
            }
            return dataRow?.date || "N/A";
        } else if (fieldType === "time") {
            const timeValue = dataRow?.time;
            if (timeValue) {
                // If it's already a time string like "13:00:00" or "01:00:00", parse it
                if (typeof timeValue === "string" && timeValue.includes(":")) {
                    const timeParts = timeValue.split(":");
                    if (timeParts.length >= 2) {
                        const hours = parseInt(timeParts[0], 10);
                        const minutes = parseInt(timeParts[1], 10);

                        // Validate hours and minutes
                        if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
                            const period = hours >= 12 ? "PM" : "AM";
                            const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                            return `${hour12.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
                        }
                    }
                }
                // If it's a valid date object or timestamp, format it
                const date = new Date(timeValue);
                if (!isNaN(date.getTime())) {
                    const timeString = date.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true
                    });
                    return timeString.replace(/a\.m\./gi, "AM").replace(/p\.m\./gi, "PM").replace(/am/gi, "AM").replace(/pm/gi, "PM");
                }
            }
            return "N/A";
        }
        return dataRow?.[fieldType] || "N/A";
    };

    // Generate page numbers for pagination (max 6 pages shown)
    // const calculateVisiblePageRange = () => {
    //     const maximumVisiblePages = 6;
    //     let visibleStartPage = Math.max(1, currentPageNumber - Math.floor(maximumVisiblePages / 2));
    //     let visibleEndPage = Math.min(totalPageCount, visibleStartPage + maximumVisiblePages - 1);
    //
    //     if (visibleEndPage - visibleStartPage + 1 < maximumVisiblePages) {
    //         visibleStartPage = Math.max(1, visibleEndPage - maximumVisiblePages + 1);
    //     }
    //
    //     return { startPage: visibleStartPage, endPage: visibleEndPage };
    // };

    return (
        <div className='simplicity-table'>
            <Paper className="table-paper-container">
                <TableContainer className="table-container">
                    <MuiTable
                        stickyHeader
                        className="table-base"
                    >
                        <TableHead>
                            <TableRow>
                                {columns?.map((columnDefinition, columnIndex) => (
                                    <TableCell
                                        key={`${columnDefinition?.id}_${columnIndex}`}
                                        className={getHeaderCellClassName(columnIndex, columns?.length || 0)}
                                        style={{
                                            minWidth: columnDefinition?.minWidth,
                                            maxWidth: columnDefinition?.maxWidth
                                        }}
                                    >
                                        {columnDefinition?.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(tableData?.length || 0) === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns?.length || 0} align="center">
                                        <Typography>No data found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tableData?.map((dataRow, rowIndex) => (
                                    <TableRow
                                        key={`${dataRow?.id}_${rowIndex}`}
                                        className={getRowClassName(rowIndex)}
                                    >
                                        {columns?.map((columnDefinition, columnIndex) => (
                                            <TableCell
                                                key={columnDefinition?.id}
                                                className={getBodyCellClassName(columnIndex, columns?.length || 0)}
                                                style={{
                                                    minWidth: columnDefinition?.minWidth,
                                                    maxWidth: columnDefinition?.maxWidth
                                                }}
                                            >
                                                {columnDefinition?.id === "evidence" ?
                                                    renderEvidenceLinks(dataRow?.[columnDefinition?.id]) :
                                                    (columnDefinition?.id === "date" || columnDefinition?.id === "time") ?
                                                        formatDateTime(dataRow, columnDefinition?.id) :
                                                        dataRow?.[columnDefinition?.id]
                                                }
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </MuiTable>
                </TableContainer>

                {/* Pagination Controls */}
                <Box className="pagination-container">

                    <Box className="pagination-wrapper">
                        <Pagination
                            count={totalPageCount}
                            page={currentPageNumber}
                            onChange={handlePageChange}
                            color="primary"
                            siblingCount={2}
                            boundaryCount={1}
                            className="pagination-base"
                            renderItem={(item) => (
                                <PaginationItem
                                    slots={{
                                        previous: () => (
                                            <Box className="pagination-button-base">
                                                <PreviousIcon />
                                            </Box>
                                        ),
                                        next: () => (
                                            <Box className="pagination-button-base">
                                                <NextIcon />
                                            </Box>
                                        )
                                    }}
                                    {...item}
                                    className={`pagination-item ${item.selected ? "pagination-selected" : ""}`}
                                />
                            )}
                        />
                        <Box className="items-per-page-container">
                            <Typography component="div" className="items-per-page-label">
                                Items per page:
                            </Typography>
                            <Dropdown
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                options={[
                                    { value: 5, label: "5" },
                                    { value: 10, label: "10" },
                                    { value: 25, label: "25" },
                                    { value: 50, label: "50" }
                                ]}
                                fullWidth={false}
                                width="86px"
                            />
                        </Box>


                    </Box>
                </Box>
            </Paper>
        </div>
    );

};

Table.displayName = "Table";
export { Table };
